"use client";

import { useContext, useId, useRef, useState, isValidElement } from "react";
import { FieldContext, FormErrorContext, validationMessage } from "./form-context";
import type {
  InputHTMLAttributes,
  ReactNode,
  Ref,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from "react";
import { useKitseed } from "../lib/provider";
import { cn } from "../lib/utils";

/**
 * Wrapping <label> keeps the control implicitly labelled, which is what the
 * accessibility tree and the Playwright `getByLabel` suites rely on.
 */
export function Field({
  label,
  compound = false,
  errorPlacement = "inline",
  hint,
  error,
  errorId,
  children,
  formatError = (message) => message,
}: {
  label: string;
  compound?: boolean;
  errorPlacement?: "inline" | "external";
  hint?: string;
  error?: string;
  errorId?: string;
  children: ReactNode;
  formatError?: (message: string) => ReactNode;
}) {
  const { messages } = useKitseed();
  const generatedId = useId();
  const form = useContext(FormErrorContext);
  const dirty = useRef(false);
  const [localError, setLocalError] = useState("");
  const child = isValidElement<{ name?: string; id?: string }>(children)
    ? children.props
    : undefined;
  const id = child?.id ?? generatedId;
  const key = child?.name ?? id;
  const message = error || form?.fields[key] || localError;
  const messageId = errorId ?? `${id}-error`;
  function validate(target: EventTarget, onlyInvalid: boolean) {
    if (
      !(
        target instanceof HTMLInputElement ||
        target instanceof HTMLSelectElement ||
        target instanceof HTMLTextAreaElement
      )
    )
      return;
    if (compound && target.name !== child?.name && target.id !== id) return;
    if (onlyInvalid) dirty.current = true;
    if ((!onlyInvalid && !dirty.current && !message) || (onlyInvalid && !message)) return;
    const next = validationMessage(target, messages);
    setLocalError(next);
    form?.setField(target.name || target.id, next);
  }
  const Wrapper = compound ? "div" : "label";
  return (
    <FieldContext.Provider
      value={{
        id,
        labelId: `${id}-label`,
        errorId: messageId,
        ...(hint ? { hintId: `${id}-hint` } : {}),
        invalid: Boolean(message),
      }}
    >
      <Wrapper
        data-field
        className="grid min-w-0 gap-1.5 [overflow-wrap:anywhere]"
        onBlur={(event) => validate(event.target, false)}
        onChange={(event) => validate(event.target, true)}
      >
        {compound ? (
          <label
            htmlFor={id}
            id={`${id}-label`}
            data-field-label
            className="text-[13px] font-medium text-fg"
          >
            {label}
          </label>
        ) : (
          <span id={`${id}-label`} data-field-label className="text-[13px] font-medium text-fg">
            {label}
          </span>
        )}
        {children}
        {message && errorPlacement === "inline" ? (
          <span id={messageId} className="text-xs text-danger-text">
            {formatError(message)}
          </span>
        ) : null}
        {hint ? (
          <span id={`${id}-hint`} className="text-xs leading-5 text-fg-subtle">
            {hint}
          </span>
        ) : null}
      </Wrapper>
    </FieldContext.Provider>
  );
}

export const fieldControlClassName = [
  "w-full min-w-0 rounded-control border border-line-strong bg-control",
  "px-2.5 text-base sm:pointer-fine:text-[13px] pointer-coarse:text-base text-fg shadow-xs outline-none",
  "transition-[border-color,box-shadow] duration-100 motion-reduce:transition-none",
  "placeholder:text-fg-subtle",
  "hover:border-[color-mix(in_oklab,var(--line-strong)_60%,var(--fg-subtle))]",
  "focus:border-brand focus:ring-2 focus:ring-[var(--ring)] focus:ring-offset-0",
  "aria-invalid:border-danger aria-invalid:ring-danger-line",
  "disabled:cursor-not-allowed disabled:bg-hover disabled:text-fg-subtle",
].join(" ");

export function Input({
  className,
  unstyled = false,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { ref?: Ref<HTMLInputElement>; unstyled?: boolean }) {
  const field = useContext(FieldContext);
  const { density } = useKitseed();
  return (
    <input
      data-density={unstyled ? undefined : density}
      data-ui-control="input"
      id={field?.id}
      {...props}
      aria-labelledby={
        props["aria-labelledby"] ?? (props["aria-label"] ? undefined : field?.labelId)
      }
      aria-invalid={props["aria-invalid"] || field?.invalid || undefined}
      aria-describedby={
        [props["aria-describedby"], field?.hintId, field?.invalid ? field.errorId : undefined]
          .filter(Boolean)
          .join(" ") || undefined
      }
      className={cn(
        !unstyled && fieldControlClassName,
        !unstyled && "h-11 sm:h-8 pointer-coarse:min-h-11 data-[density=comfortable]:h-11",
        className,
      )}
    />
  );
}

export function Textarea({
  className,
  unstyled = false,
  ...props
}: TextareaHTMLAttributes<HTMLTextAreaElement> & { unstyled?: boolean }) {
  const field = useContext(FieldContext);
  const { density } = useKitseed();
  return (
    <textarea
      data-density={unstyled ? undefined : density}
      id={field?.id}
      {...props}
      aria-labelledby={
        props["aria-labelledby"] ?? (props["aria-label"] ? undefined : field?.labelId)
      }
      aria-invalid={props["aria-invalid"] || field?.invalid || undefined}
      aria-describedby={
        [props["aria-describedby"], field?.hintId, field?.invalid ? field.errorId : undefined]
          .filter(Boolean)
          .join(" ") || undefined
      }
      className={cn(
        !unstyled && fieldControlClassName,
        !unstyled && "min-h-20 resize-y py-2 leading-6",
        className,
      )}
    />
  );
}

/**
 * Deliberately a native <select>: it gives free mobile pickers, keyboard
 * behaviour and `selectOption()` support. Only the chevron is customised.
 */
export function Select({
  className,
  unstyled = false,
  ...props
}: SelectHTMLAttributes<HTMLSelectElement> & { unstyled?: boolean }) {
  const field = useContext(FieldContext);
  const { density } = useKitseed();
  return (
    <select
      data-ui-control="select"
      data-density={unstyled ? undefined : density}
      id={field?.id}
      {...props}
      aria-labelledby={
        props["aria-labelledby"] ?? (props["aria-label"] ? undefined : field?.labelId)
      }
      aria-invalid={props["aria-invalid"] || field?.invalid || undefined}
      aria-describedby={
        [props["aria-describedby"], field?.hintId, field?.invalid ? field.errorId : undefined]
          .filter(Boolean)
          .join(" ") || undefined
      }
      className={cn(
        !unstyled && fieldControlClassName,
        !unstyled &&
          "h-11 sm:h-8 pointer-coarse:min-h-11 data-[density=comfortable]:h-11 cursor-pointer appearance-none bg-no-repeat pr-8",
        !unstyled && "bg-[position:right_0.5rem_center] bg-[size:1rem]",
        !unstyled && "bg-[image:var(--select-chevron)]",
        className,
      )}
    />
  );
}

/** Checkbox/radio row used by the digest and channel pickers. */
export function ChoiceRow({
  children,
  className,
  unstyled = false,
  inputClassName,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & {
  children: ReactNode;
  unstyled?: boolean;
  inputClassName?: string;
}) {
  return (
    <label
      data-ui-control="choice"
      className={cn(
        !unstyled &&
          "flex cursor-pointer items-start gap-2.5 rounded-control border border-line bg-panel p-2.5",
        !unstyled &&
          "min-h-11 transition-colors duration-100 motion-reduce:transition-none hover:border-line-strong hover:bg-hover",
        !unstyled && "has-checked:border-brand-line has-checked:bg-brand-subtle",
        !unstyled &&
          "has-focus-visible:outline-none has-focus-visible:ring-2 has-focus-visible:ring-brand-line has-disabled:cursor-not-allowed has-disabled:opacity-60",
        className,
      )}
    >
      <input
        type={unstyled ? undefined : "checkbox"}
        {...props}
        className={cn(
          !unstyled && "mt-0.5 size-3.5 shrink-0 cursor-pointer accent-brand",
          inputClassName,
        )}
      />
      <span className="min-w-0 flex-1">{children}</span>
    </label>
  );
}
